import { useEffect, useMemo } from 'react';
import { useUser } from '../providers/UserProvider';
import { useAssignableMembers } from './useRoles';
import {
    assigneeFilterApiValues,
    buildAssigneeFilterOptions,
    migrateAssigneeFilterValues
} from '../utils/assigneeFilter';

const sameValues = (a: readonly string[], b: readonly string[]) =>
    a.length === b.length && a.every((value, i) => value === b[i]);

// A page's assignee filter over everyone at ODO; `unassigned` is its API's "nobody" token and
// `onMigrate` rewrites stored values once legacy labels resolve (or are dropped).
export function useAssigneeFilter(
    values: readonly string[],
    unassigned: string,
    onMigrate?: (values: string[]) => void
) {
    const { user } = useUser();
    const { members, isLoading } = useAssignableMembers();
    const options = useMemo(
        () => buildAssigneeFilterOptions(members, user?.email),
        [members, user?.email]
    );
    const peopleLoaded = !isLoading && members.length > 0;
    // Keeps `values` itself when nothing changes, so apiValues only changes identity with the filter.
    const current = useMemo(() => {
        if (!peopleLoaded) return values;
        const migrated = migrateAssigneeFilterValues(values, members);
        return sameValues(migrated, values) ? values : migrated;
    }, [peopleLoaded, values, members]);
    const needsMigration = current !== values;
    useEffect(() => {
        if (needsMigration) onMigrate?.([...current]);
    }, [needsMigration, current, onMigrate]);
    const apiValues = useMemo(
        () => assigneeFilterApiValues(current, user?.email, unassigned),
        [current, user?.email, unassigned]
    );
    return { options, apiValues, settled: values.length === 0 || !isLoading };
}
